/**
  * @class msysAbstractClient
  * @description
  * Implements base structure of web service client that connects to web service server.
  * Not intended for direct use. Used by msys*Client modules as base class.
  */

import msysListener from 'msysListener'
const WS = require('ws')
const mcrypt = require('msysCrypt')
const ms = require('msysMessage')
const https = require('https')
const PING_DATA = new Uint8Array(['?'.charCodeAt(0)])
const PONG_DATA = new Uint8Array(['!'.charCodeAt(0)])

export default class msysAbstractClient extends msysListener {
  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates an instance of the class.
  * @param {string} [id] - ID under which the object will listen for messages
  * @param {string} [cfg] - object initial configuration
  * @param {string} [cfg_schema] - additional schema properties from derived classes
  * @param {string} [dependencies] - additional dependencies from derived classes
  */
  constructor(id, cfg, cfg_schema, dependencies) {
    dependencies = _.union(['msysLogger'], dependencies)
    cfg_schema = _.extend({
      servers: {
        description: 'list of allowed server URLs',
        type: 'array',
        items: { type: 'string' },
        default: [],
      },
      randomize_servers: {
        description: 'flag whether to randomize order of servers at the start',
        type: 'boolean',
        default: false,
      },
      ssl_allow_self_signed: {
        description: 'if true, enables self-signed certificates for WSS server',
        type: 'boolean',
        default: false,
      },
      app_key_password: {
        description: 'password for app private key',
        type: 'string',
        default: 'Vylepseny4NEV',
      },
      connection_timeout: {
        description: 'connection timeout in seconds; used to timeout initial connection',
        type: 'number',
        default: 10,
      },
      reconnection_delay: {
        description: 'reconnection delay in seconds',
        type: 'number',
        default: 5,
      },
      ping_interval: {
        description: 'ping interval in seconds',
        type: 'number',
        default: 1,
      },
      alive_timeout: {
        description: 'if no data/ping came within this timeout, reconnect; used to timeout after successful connection',
        type: 'number',
        default: 5,
      },
      md5: {
        description: 'md5 hash reported to server',
        type: 'string',
      },
      ignore_server_certificate: {
        description: 'If true, client will not check server certificate against own CA root',
        type: 'boolean',
        default: false,
      },
    }, cfg_schema)
    super(id, cfg, cfg_schema, dependencies)
    this.PROTOCOL_VERSION = 1
    this.action_connected = ''
    this.action_disconnected = ''
  } // constructor

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Received automatically (through inheritance of msysListener) when configuration and all dependencies were fulfilled.
  * Note that dependancy is fulfilled also when it exited instead of started.
  * @param {object} sender - Sender of the message
  * @param {object} params - not used
  */
  mmStart(sender, params) {
    if(this.config.md5) this.info('Client MD5: '+this.config.md5)
    if(this.config.randomize_servers) { this.config.servers = _.shuffle(_.toArray(this.config.servers)) }
    this.config.configured_servers = _.cloneDeep(this.config.servers)
    super.mmStart(sender, params)
  } // mmStart

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Request to exit the listener
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {boolean} [params.delete] - if true, will be deleted from list of listeners
  * @param {string} [params.error] - reason for exit
  */
  mmExit(sender, params) {
    this.debug('CDBG mmExit: disconnectServer')
    this.disconnectServer()
    super.mmExit(sender, params)
  } // mmExit

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Connect to server. Disconnects if connection exists.
  * @param {boolean} same_server - default false; whether to connect to same as before or subsequent server from the list of servers
  */
  connectServer(server_idx) {
    const servers = _.toArray(this.config.servers).filter((url)=>{ return /^wss?:\/\//.test(url) })
    if(servers.length===0) {
      this.warn('No servers configured; aborting connection attempts')
      return
    }
    server_idx = ((parseInt(server_idx) || 0) % servers.length) || 0
    this.debug('CDBG connectServer: '+server_idx)
    this.server_idx = server_idx
    this.disconnectServer()

    // this is to ensure reconnect unless connected in configured time
    this.debug('CDBG connectServer: creating connection timeouter for '+this.config.connection_timeout+' ms')
    this.timeouter = setTimeout(()=>{
      this.debug('CDBG connectServer: fired tconnection imeouter; before onSocketTimeout '+server_idx)
      this.onSocketTimeout()
      this.debug('CDBG connectServer: fired connection timeouter; after onSocketTimeout '+server_idx)
    }, 1000*parseInt(this.config.connection_timeout))

    const server_url = servers[server_idx]
    this.debug('CDBG connectServer: creating ws for server '+server_url+'   '+server_idx)
    const is_node_ws = typeof WS.Server === 'function' // Node.js ws library has Server class, browser WebSocket does not
    const WS_ALLOW_SELF_SIGNED = is_node_ws && /^wss:/.test(server_url) && this.config.ssl_allow_self_signed && https?.Agent ? { agent: https.Agent({ rejectUnauthorized: false }) } : null
    const ws = this.ws = WS_ALLOW_SELF_SIGNED ? new WS(server_url, { rejectUnauthorized: false }) : new WS(server_url)
    ws.binaryType = 'arraybuffer'
    ws.messagesSent = 0
    ws.messagesReceived = 0
    ws.bytesReceived = 0
    ws.bytesSent = 0
    ws.onopen = (event)=>this.onSocketOpen(event)
    ws.onmessage = (event)=>this.onSocketMessage(event)
    ws.onclose = ()=>this.onSocketClose()
    ws.onerror = (err)=>this.debug('CDBG ws error for server '+server_url+'/'+server_idx+': '+err.message)
    // this.debug('connectServer: created ws for server '+server_url+'   '+server_idx)
  } // connectServer

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Disconnect from server
  */
  disconnectServer() {
    const ws = this.ws
    this.ws = null
    if(ws && ws.wasOpened) {
      this.info(`Connection to server closed; sent ${ws.messagesSent} messages / ${ws.bytesSent} bytes; received ${ws.messagesReceived} messages / ${ws.bytesReceived} bytes`)
    }
    if(this.timeouter) {
      this.debug('CDBG disconnectServer clearing timeouter')
      clearTimeout(this.timeouter)
      this.timeouter=null
    }
    if(this.aliver) {
      this.debug('CDBG disconnectServer clearing aliver')
      clearInterval(this.aliver)
      this.aliver=null
    }
    if(this.pinger) {
      this.debug('CDBG disconnectServer clearing pinger')
      clearInterval(this.pinger)
      this.pinger=null
    }
    if(ws) {
      ws.onopen = ws.onmessage = ws.onclose = ()=>{}
      this.debug('CDBG disconnectServer clearing ws')
      if(ws.app_id && this.action_disconnected) {
        this.publish('#ALLWITHME', this.action_disconnected, { url: ws.url })
      }
      this.debug('CDBG disconnectServer clearing ws.onmessage')
      try{
        this.debug('CDBG disconnectServer before ws.close')
        ws.close()
        this.debug('CDBG disconnectServer after ws.close')
      }catch(e) {
        this.error(e.stack)
        this.debug('CDBG disconnectServer ws.close error: '+e.message)
      }
    }
  } // disconnectServer

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Disconnects from server and connects again.
  * @param {boolean} next_server - whether to try previously used server or next server in the list
  * @param {boolean} delayed - whether to delay the start; this is automatically true if next server is required and that will be the the first in the list (to pause after all servers tried)
  * @param {string} reason - error reason to be logged
  */
  reconnectServer(next_server, delayed, error) {
    if(this.connection_paused) return
    this.debug(`CDBG reconnectServer: next_server=${next_server} delayed=${delayed} error=${error}`)
    this.disconnectServer()
    if(error) {
      this.debug('CDBG reconnectServer called with error: '+error)
      this.error(error)
    }

    const servers = _.toArray(this.config.servers).filter((url)=>{ return /^wss?:\/\//.test(url) })
    if(servers.length===0) {
      this.warn('No servers configured; aborting connection attempts')
      return
    }
    this.debug('CDBG reconnectServer servers: ['+servers.join(', ')+']')
    let server_idx = parseInt(this.server_idx) || 0
    if(next_server) server_idx++
    server_idx = (server_idx % servers.length) || 0

    delayed = delayed || (next_server && server_idx===0)
    const delay = delayed ? 1000*this.config.reconnection_delay : 10

    this.debug('CDBG reconnectServer to server '+server_idx+' delayed for '+delay+' ms')
    setTimeout(()=>{
      this.connectServer(server_idx)
    }, delay)
  } // reconnectServer

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Handle newly open connection
  * @param {object} event - MessageEvent
  */
  onSocketOpen(event) {
    if(this.ws) this.ws.wasOpened = true
    const target = (event ? event.target : null) || {}
    this.debug('CDBG onSocketOpen: readyState='+target.readyState+' url='+target.url)

    // setup periodic ping/pong
    if(this.pinger) {
      this.error('onSocketOpen: pinger already exists')
      clearInterval(this.pinger)
      this.pinger = null
    }
    this.debug('CDBG onSocketOpen: creating pinger')

    this.pinger = setInterval(()=>{
      if(!this.ws) {
        this.debug('CDBG pinger: this.ws is empty')
        return
      }
      try{
        this.debug('CDBG pinger: sending ping')
        try {
          // our "ping" message
          this.sendPing()
        }catch(e) {
          this.error('Failed ws.send')
        }
      }catch(e) {
        this.error(`Error sending ping to server: ${e}`)
      }
    }, this.config.ping_interval*1000)

    // aliver periodically check if we have received some data orping from server
    if(this.aliver) {
      this.error('onSocketOpen: aliver already exists')
      clearInterval(this.aliver)
      this.aliver = null
    }

    this.aliver = setInterval(()=>{
      const age = Date.now() - (parseInt(this.last_incoming_ms) || 0)
      if(age > this.config.alive_timeout*1000) {
        this.debug('CDBG aliver: no alive, reconnecting')
        this.reconnectServer(false, true)
      }
    }, 1000)

    // set last_incoming_ms to false, expecting to be changed to true in this.config.alive_timeout seconds
    this.last_incoming_ms = Date.now()
  } // onSocketOpen

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Send a ping to websocket server
  */
  sendPing() {
    if(this.ws) {
      this.ws.send(PING_DATA)
    }
  } // sendPing

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Handle connection timeout
  */
  onSocketTimeout() {
    this.debug('CDBG onSocketTimeout, going to reconnectServer: next_server,immediately')
    this.reconnectServer(true, false) // reconnect immediately to next server
  } // onSocketTimeout

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Handle closing of the connection to server
  */
  onSocketClose() {
    if(this.ws && this.ws.app_id) {
      this.debug('CDBG onSocketClose appid='+this.ws.app_id+', going to reconnectServer: same_server,delayed')
      this.reconnectServer(false, true) // reconnect to same server with delay
    }else{
      this.debug('CDBG onSocketClose noappid, this.ws='+!!this.ws+', going to reconnectServer: next_server,immediately')
      this.reconnectServer(true, false) // reconnect immediately to next server
    }
  } // onSocketClose

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Called when received message through websocket
  * @param {object} ws - web socket receiving data
  * @param {string} data - received data
  * @param {string} flags - currently not used
  */
  onSocketMessage(event) {
    if(!this.ws) return
    const received_dt = unow()
    this.ws.messagesReceived++
    const data = event.data
    if(!(data instanceof ArrayBuffer)) return this.error('Received non-binary message from server')
    this.ws.bytesReceived += data.byteLength
    const uint8 = new Uint8Array(data)

    this.last_incoming_ms = Date.now()

    // we use this as ping/pong replacement (as browsers don't support ping requests)
    if(uint8.length===1 && uint8[0]===PONG_DATA[0]) {
      this.debug('CDBG onSocketMessage: received pong')
      return
    }

    const msg = ms.uint2msg(uint8)
    if(!msg) return this.error('Received invalid message from server')
    const { sender, target, action, params } = msg

    // special handling of identification
    try{
      if(action==='mxChallenge') return this.mxChallenge(params)
      if(action==='mxIdentified') {
        const client_time = unow()
        const server_time = params.event_dt
        const time_diff = client_time - server_time
        const client_dt = new Date(Math.floor(client_time/1000))
        const server_dt = new Date(Math.floor(server_time/1000))
        const client_dt_str = client_dt.toLocaleTimeString('lt-lt')+'.'+String(client_time % 1_000_000).padStart(6, '0')
        const server_dt_str = server_dt.toLocaleTimeString('lt-lt')+'.'+String(server_time % 1_000_000).padStart(6, '0')
        this[Math.abs(time_diff)>10_000_000 ? 'warn' : 'debug']('My time:', client_dt_str, 'Server time:', server_dt_str, 'Time difference:', (time_diff/1000).toFixed(3), 'ms')
        return this.mxIdentified(params)
      }
      if(action==='mxEnrolling') return this.mxEnrolling(params)
      if(action==='mxError') return this.mxError(params)
    }catch(e) {
      return this.error('Error invoking mx command'+action)
    }

    if(!this.ws || !this.ws.app_id) return this.error('Received message from unidentified server')
    if(!sender) return this.error('Received message with missing sender from server')
    if(!target) return this.error('Received message with missing target from server')
    if(!action) return this.error('Received message with missing action from server')
    params._meta = { bytes: uint8.length, received_dt }
    this.mxMessage(sender, target, action, params)
  } // onSocketMessage

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Incoming message from server
  * @param {object} sender - sender
  * @param {object} target - target
  * @param {string} action - message action
  * @param {object} params - message params
  */
  mxMessage(sender, target, action, params) {
    // derived class must overwrite this method and do something like: this.publishAs(sender, target, action, params)
    this.error(`Missing mxMessage handler for action ${action}`)
  } // mxMessage

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Send a message to websocket server
  * @param {object} msg - message to be sent
  */
  sendToServer(msg) {
    if(!this.ws || this.ws.readyState!==WS.OPEN) {
      this.error('sendToServer while not connected: ws='+!!this.ws+'  readyState='+(this.ws?this.ws.readyState:null))
      return false
    }

    const uint = ms.msg2uint(msg)
    if(!uint) return this.error('Cannot binarize message')
    try{
      this.ws.send(uint)
      this.ws.messagesSent++
      this.ws.bytesSent += msg.byteLength
      return true
    }catch(e) {
      this.error(`sendToServer: Error sending to server: ${e}`)
      return false
    }
  } // sendToServer

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Server requests client application to authenticate itself
  * @param {string} params.challenge - random challenge string to be returned signed
  */
  mxChallenge(params) {
    this.debug('CDBG mxChallenge')
    this.client_challenge = mcrypt.challenge()
    const challenge = params.challenge + this.client_challenge
    const signature = mcrypt.sign(mcrypt.privateFromPEM(this.app_key, this.config.app_key_password), challenge)
    if(this.ws) {
      this.ws.app_protocol = params.protocol
      this.ws.ident = params.server
      this.ws.my_ip_from_server = _.toString(params.clientip)
    }
    const identify = {
      client: MOSY_APP_WITH_VERSION,
      protocol: this.PROTOCOL_VERSION,
      time: Date.now(),
      timezone: new Date().getTimezoneOffset(),
      challenge: this.client_challenge,
      signature,
      cert: this.app_cert,
    }
    if(this.config.md5) identify.md5 = this.config.md5
    this.sendToServer({
      action: 'mxIdentify',
      params: identify,
    })
  } // mxChallenge

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Server requests client application to autenticate itself
  * @param {string} challenge - random challenge string to be returned signed
  */
  mxEnrolling(params) {
  } // mxEnrolling

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Server don't know provided app_id, we have to enroll
  * @param {string} params.reason - reason for error
  */
  mxError(params) {
    this.debug('mxError: '+JSON.stringify(params))
    this.debug('CDBG mxError: reconnectServer next_server, delayed')
    if(this.action_disconnected) {
      const url = this.ws?.url ?? ''
      const out = { url }
      if(params.reason) out.reason = params.reason
      this.publish('#ALL', this.action_disconnected, out)
    }
    this.reconnectServer(true, true, 'Communication error: '+(params?params.reason:null))
  } // mxError

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Server authorizes the application
  * @param {string} cert - server's application certificate
  * @param {string} challenge - random challenge string from server
  * @param {string} signature - signature of server+client challenge
  */
  mxIdentified(params) {
    this.debug('CDBG mxIdentified received')
    // stop timeouter
    if(this.timeouter) {
      this.debug('CDBG mxIdentified: stopping connection timeouter')
      clearTimeout(this.timeouter)
      this.timeouter=null
    }

    // just for sure, check if we have our part of challenge set
    if(!this.client_challenge) {
      this.error('mxIdentified: missing challenge')
      return this.reconnectServer(true, false, 'missing challenge')
    }

    // verify server's certificate with our's
    const server_info = mcrypt.verifyCertificateWithMyCA(this.app_cert, params.cert, this.config.ignore_server_certificate)
    if(!server_info) {
      this.error('mxIdentified: cannot determine server_info from certificate')
      return this.reconnectServer(true, false, 'invalid server certificate')
    }
    if(!server_info.app_id) {
      this.error('mxIdentified: cannot determine server app_id from certificate')
      return this.reconnectServer(true, false, 'invalid server app_id')
    }

    // check if provided signature of server+our challenge is signed by private key corresponding to provided public key
    const challenge = params.challenge + this.client_challenge
    if(!mcrypt.verifySignature(server_info.pub_key, challenge, params.signature)) {
      this.error('mxIdentified: cannot verify server signature with certificate')
      return this.reconnectServer(true, false, 'invalid server signature')
    }

    // set channel as identified and inform all applications
    this.ws.app_id = server_info.app_id
    const SERVER_TYPES = { B: 'peering', R: 'peering', S: 'domain' }
    const server_type = SERVER_TYPES[String(server_info.app_id).substring(0, 1)]
    this.info('Connected to '+(server_type?server_type+' ':'')+'server '+server_info.app_id+' '+this.ws.url)
    if(this.action_connected) {
      setTimeout(()=>{
        this.publish('#ALL', this.action_connected, { app_id: this.ws.app_id, url: this.ws.url, ident: this?.ws?.ident || '', client_ip: this?.ws?.my_ip_from_server || '' })
      }, 10)
    }
  } // mxIdentified

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * enables online change of configured servers
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {array} [params.servers] - list of servers; if not specified, servers will revert to what has been configured for the application
  * @param {boolean} [params.reconnect] - whether reconnect should be done immediately
  */
  mmChangeServers(sender, params) {
    const { servers, reconnect } = params
    if(Array.isArray(servers)) {
      // setup new servers
      this.config.servers = params.servers
    }else{
      // revert back to configured list of servers
      this.config.servers = this.config.configured_servers
    }
    if(reconnect) {
      this.server_idx = 0
      this.disconnectServer()
      this.reconnectServer(false, false)
    }
  } // mmChangeServers

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * forces client to break connection with server and don't reconnect until mmConnectionResume or given time expired; used for debugging
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {integer} [params.duration] - if given, after this time (in milliseconds) mmConnectionResume will be automatically initiated
  */
  mmConnectionPause(sender, params) {
    this.connection_paused = true
    if(this.connection_resume_timer) {
      clearTimeout(this.connection_resume_timer)
      delete this.connection_resume_timer
    }

    this.disconnectServer()

    if(params.duration>0) { this.connection_resume_timer = setTimeout(()=>this.publish('#ME', 'mmConnectionResume'), params.duration) }
  } // mmConnectionPause

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * resumes broken connection with server and don't reconnect until mmServerResume or given time expired; used for debugging
  * @param {object} sender - message sender
  * @param {object} params - not used
  */
  mmConnectionResume(sender, params) {
    delete this.connection_paused
    if(this.connection_resume_timer) {
      clearTimeout(this.connection_resume_timer)
      delete this.connection_resume_timer
    }
    this.reconnectServer(false, false)
  } // mmConnectionResume
} // msysAbstractClient
